// src/pages/finance-portal/budgets/Budgets.js
//
// Budgets list page

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Chip, IconButton, Alert, Skeleton, Tabs, Tab,
    Tooltip, InputAdornment,
    Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import BudgetForm from './BudgetForm';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const STATUS_COLORS = {
    DRAFT:     { bg: '#f1f5f9', color: '#64748b' },
    IN_REVIEW: { bg: '#fef9c3', color: '#854d0e' },
    APPROVED:  { bg: '#dcfce7', color: '#166534' },
    REJECTED:  { bg: '#fecaca', color: '#991b1b' },
    CLOSED:    { bg: '#e0e7ff', color: '#4338ca' },
};

const STATUS_TABS = ['ALL', 'DRAFT', 'IN_REVIEW', 'APPROVED', 'CLOSED', 'REJECTED'];

function fmtMoney(val, currency = 'USD') {
    return new Intl.NumberFormat('en-US', {
        style: 'currency', currency, maximumFractionDigits: 0,
    }).format(val || 0);
}

export default function Budgets() {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [budgets, setBudgets] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [statusTab, setStatusTab] = useState(0);

    const [formOpen, setFormOpen] = useState(false);
    const [editBudget, setEditBudget] = useState(null);
    const [deleteDialog, setDeleteDialog] = useState({ open: false, budget: null });
    const [deleting, setDeleting] = useState(false);

    const fetchBudgets = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = { page: page + 1, per_page: rowsPerPage };
            const currentStatus = STATUS_TABS[statusTab];
            if (currentStatus !== 'ALL') params.status = currentStatus;
            if (search) params.search = search;

            const res = await axiosInstance.get('/api/finance/budgets', { params });
            if (res.data.success) {
                setBudgets(res.data.data);
                setTotal(res.data.total);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load budgets');
        } finally {
            setIsLoading(false);
        }
    }, [page, rowsPerPage, statusTab, search]);

    useEffect(() => { fetchBudgets(); }, [fetchBudgets]);

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await axiosInstance.delete(`/api/finance/budgets/${deleteDialog.budget.id}`);
            enqueueSnackbar('Budget deleted', { variant: 'success' });
            setDeleteDialog({ open: false, budget: null });
            fetchBudgets();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to delete', { variant: 'error' });
        } finally {
            setDeleting(false);
        }
    };

    const handleAddNew = () => { setEditBudget(null); setFormOpen(true); };
    const handleEdit = (e, b) => { e.stopPropagation(); setEditBudget(b); setFormOpen(true); };
    const handleRowClick = (b) => navigate(`/finance-portal/budgets/${b.id}`);

    const labelCell = { fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b',
                        textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0',
                        letterSpacing: '0.05em' };
    const dataCell = { fontFamily, fontSize: '0.85rem', color: '#475569' };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex',
                          alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)' }}>
                            <AccountBalanceIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white',
                                                          fontSize: { xs: '1.4rem', md: '1.85rem' }, lineHeight: 1.2 }}>
                                Budgets
                            </Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)',
                                              fontSize: '0.9rem', mt: 0.25 }}>
                                Plan and track your fiscal year budgets
                            </Typography>
                        </Box>
                    </Box>
                    <Button
                        variant="contained" startIcon={<AddIcon />} onClick={handleAddNew}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                              bgcolor: 'rgba(255,255,255,0.2)', color: '#fff',
                              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
                    >
                        New Budget
                    </Button>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)}
                                  sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        size="small" placeholder="Search by name or budget number..."
                        value={search}
                        onChange={e => { setSearch(e.target.value); setPage(0); }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
                            sx: { fontFamily, fontSize: '0.85rem', borderRadius: '10px' },
                        }}
                        sx={{ minWidth: { xs: '100%', sm: 320 } }}
                    />
                </Box>

                <Tabs
                    value={statusTab}
                    onChange={(_, val) => { setStatusTab(val); setPage(0); }}
                    sx={{
                        mb: 2,
                        '& .MuiTab-root': { fontFamily, fontWeight: 600, textTransform: 'none',
                                             fontSize: '0.85rem', color: '#64748b', minHeight: 40, py: 1 },
                        '& .Mui-selected': { color: ACCENT },
                        '& .MuiTabs-indicator': { bgcolor: ACCENT, height: 3, borderRadius: '3px' },
                        minHeight: 40,
                    }}
                >
                    {STATUS_TABS.map(tab => (
                        <Tab key={tab} label={tab.replace('_', ' ')} />
                    ))}
                </Tabs>

                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Budget #', 'Name', 'Fiscal Year', 'Period', 'Status',
                                      'Active Version', 'Total', 'Actions'].map(h =>
                                        <TableCell key={h} sx={labelCell}>{h}</TableCell>
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [1,2,3,4,5].map(i => (
                                        <TableRow key={i}><TableCell colSpan={8}><Skeleton height={36} /></TableCell></TableRow>
                                    ))
                                ) : budgets.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6 }}>
                                            <AccountBalanceIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.95rem' }}>No budgets found</Typography>
                                            <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.8rem', mt: 0.5 }}>
                                                Create your first budget to start planning
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : budgets.map(b => {
                                    const statusStyle = STATUS_COLORS[b.status] || STATUS_COLORS.DRAFT;
                                    return (
                                        <TableRow key={b.id} hover onClick={() => handleRowClick(b)}
                                                  sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f8fafc' } }}>
                                            <TableCell sx={{ ...dataCell, fontWeight: 600, color: '#1e293b' }}>
                                                {b.budget_number}
                                            </TableCell>
                                            <TableCell sx={{ ...dataCell, color: '#1e293b', fontWeight: 500 }}>
                                                {b.name}
                                            </TableCell>
                                            <TableCell sx={dataCell}>{b.fiscal_year}</TableCell>
                                            <TableCell sx={{ ...dataCell, fontSize: '0.78rem' }}>
                                                {b.period_start} → {b.period_end}
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={b.status.replace('_', ' ')} size="small"
                                                      sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600,
                                                            height: 22, bgcolor: statusStyle.bg, color: statusStyle.color }} />
                                            </TableCell>
                                            <TableCell sx={{ ...dataCell, fontSize: '0.78rem' }}>
                                                {b.active_version_label || '—'}
                                            </TableCell>
                                            <TableCell sx={{ ...dataCell, fontWeight: 600, color: '#1e293b' }}>
                                                {fmtMoney(b.total_amount, b.currency)}
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    <Tooltip title="Edit">
                                                        <IconButton size="small" onClick={(e) => handleEdit(e, b)}
                                                                    sx={{ color: '#94a3b8', '&:hover': { color: '#3b82f6' } }}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    {(b.status === 'DRAFT' || b.status === 'REJECTED') && (
                                                        <Tooltip title="Delete">
                                                            <IconButton size="small"
                                                                        onClick={(e) => { e.stopPropagation(); setDeleteDialog({ open: true, budget: b }); }}
                                                                        sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}>
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div" count={total} page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily } }}
                    />
                </Card>
            </Box>

            <BudgetForm
                open={formOpen}
                onClose={() => { setFormOpen(false); setEditBudget(null); }}
                onSaved={fetchBudgets}
                budget={editBudget}
            />

            <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, budget: null })}
                    PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                    Delete {deleteDialog.budget?.budget_number}?
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.9rem' }}>
                        This will permanently delete this budget and all its versions and lines. This cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDeleteDialog({ open: false, budget: null })}
                            sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" onClick={handleDelete} disabled={deleting}
                            sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                  bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}>
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
